import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";
import { ProfileEditDialog } from "@/components/ProfileEditDialog";
import { ProjectPhotoUploadDialog } from "@/components/ProjectPhotoUploadDialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, FolderKanban, Trophy, Briefcase, Clock, Pencil, Camera, ImageIcon } from "lucide-react";

const REGION_LABELS = { centro: "Zona Centro", norte: "Zona Norte", bajio: "Bajío", otros: "Otros estados" };

export default function EmployeeProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, updateUser } = useAuth();
  const [emp, setEmp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectDialogId, setProjectDialogId] = useState(null);

  const isMe = !!(user && emp && String(user.id) === String(emp.id));

  const load = () => {
    api.get(`/employees/${id}`).then(({ data }) => setEmp(data)).catch(() => setNotFound(true));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (isMe) {
      api.get("/users/me/projects").then(({ data }) => setProjects(data)).catch(() => setProjects([]));
    }
  }, [isMe]);

  const onSaved = (freshUser) => {
    updateUser({ name: freshUser.name, alias: freshUser.alias, photo: freshUser.photo, display_name: freshUser.display_name, current_project_id: freshUser.current_project_id });
    // reflect in the current profile view
    setEmp((e) => (e ? { ...e, name: freshUser.name, photo: freshUser.photo } : e));
  };

  if (notFound) return <div className="text-sm text-[#5C6A7D]">Colaborador no encontrado.</div>;
  if (!emp) return <div className="text-sm text-[#5C6A7D]">Cargando perfil…</div>;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="employee-profile-page">
      <Link to="/employees" className="inline-flex items-center text-xs uppercase tracking-[0.18em] text-[#5C6A7D] hover:text-[#0A2540]">
        <ArrowLeft size={14} className="mr-1" /> People
      </Link>

      {/* Header */}
      <div className="bg-gradient-to-br from-[#F0FAF4] via-white to-[#EFF5FF] border border-[#16A34A]/15 rounded-3xl p-7 sm:p-9">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left">
          {/* Avatar (clickable when it's the current user) */}
          {isMe ? (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="relative group rounded-full focus:outline-none focus:ring-2 focus:ring-[#10B981]/50"
              data-testid="profile-avatar-edit"
              aria-label="Cambiar avatar"
            >
              <Avatar name={emp.name} photo={emp.photo} size={96} className="shadow-md" />
              <span className="absolute inset-0 rounded-full flex items-center justify-center bg-black/45 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera size={22} strokeWidth={1.75} />
              </span>
            </button>
          ) : (
            <Avatar name={emp.name} photo={emp.photo} size={96} className="shadow-md" />
          )}
          <div className="flex-1">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter">{emp.name}</h1>
            <div className="text-[#2563EB] font-semibold mt-1 flex items-center justify-center sm:justify-start gap-1.5"><Briefcase size={15} /> {emp.position}</div>
            <div className="flex items-center justify-center sm:justify-start flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-[#5C6A7D]">
              {emp.current_project && <span className="inline-flex items-center gap-1.5"><FolderKanban size={14} className="text-[#2563EB]" /> {emp.current_project.name}</span>}
              {emp.region && <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#16A34A]" /> {REGION_LABELS[emp.region] || emp.region}</span>}
              {emp.years_in_company != null && <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {emp.years_in_company} años en la empresa</span>}
            </div>
            {isMe && (
              <div className="mt-4 flex justify-center sm:justify-start">
                <Button
                  variant="outline"
                  onClick={() => setEditOpen(true)}
                  className="rounded-sm h-9 text-xs uppercase tracking-[0.15em]"
                  data-testid="profile-edit-button"
                >
                  <Pencil size={13} className="mr-1.5" /> Modificar datos
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contribution stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="employee-stats">
        {[
          { label: "Proyectos participados", value: emp.projects_count, icon: FolderKanban, accent: "#2563EB" },
          { label: "Puntos acumulados", value: emp.total_points.toLocaleString(), icon: Trophy, accent: "#F59E0B" },
          { label: "Proyectos actuales", value: emp.current_project ? 1 : 0, icon: FolderKanban, accent: "#16A34A" },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${s.accent}14`, color: s.accent }}><s.icon size={18} strokeWidth={1.75} /></div>
            <div className="font-display font-extrabold text-3xl text-[#0A2540] mt-4 leading-none">{s.value}</div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C6A7D] mt-2">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Own-profile only: participated projects with photo upload */}
      {isMe && (
        <div data-testid="my-participated-projects">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Mi espacio</div>
              <h2 className="font-display text-xl font-bold text-[#0A2540] mt-0.5">Proyectos participados</h2>
            </div>
            <div className="text-xs text-[#5C6A7D]">Toca un proyecto para subir fotos</div>
          </div>
          {projects.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 text-center text-sm text-[#5C6A7D]">
              Aún no estás dado de alta en ningún proyecto.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="participated-projects-grid">
              {projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setProjectDialogId(p.id)}
                  className="text-left bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:border-[#16A34A]/30 hover:shadow-[0_12px_28px_rgba(22,163,74,0.10)]"
                  data-testid={`participated-project-${p.id}`}
                >
                  <div className="aspect-[16/6] bg-[#EAF0F6] overflow-hidden">
                    {p.hero_image ? (
                      <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#94A3B8]"><ImageIcon size={22} /></div>
                    )}
                  </div>
                  <div className="p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0A2540] truncate">{p.name}</div>
                      <div className="text-xs text-[#5C6A7D] mt-0.5 truncate">
                        {REGION_LABELS[p.region] || "—"}{p.location ? ` · ${p.location}` : ""}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-display font-extrabold text-lg text-[#0A2540] leading-none">{p.gallery_count}</div>
                      <div className="text-[9px] uppercase tracking-[0.14em] text-[#5C6A7D] mt-1">Fotos</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dialogs (own profile only) */}
      {isMe && (
        <>
          <ProfileEditDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            user={{ name: emp.name, alias: user?.alias, photo: emp.photo, current_project_id: user?.current_project_id }}
            onSaved={(fresh) => {
              onSaved(fresh);
              // Refresh the participated-projects list in case the user changed their assigned project.
              api.get("/users/me/projects").then(({ data }) => setProjects(data)).catch(() => {});
            }}
          />
          <ProjectPhotoUploadDialog
            open={!!projectDialogId}
            onOpenChange={(o) => !o && setProjectDialogId(null)}
            projectId={projectDialogId}
            onUploaded={({ project_id, gallery }) => {
              setProjects((prev) => prev.map((p) => (p.id === project_id ? { ...p, gallery_count: gallery.length } : p)));
            }}
          />
        </>
      )}
    </div>
  );
}
